import axios from 'axios'
import queryString from 'query-string'

import apiConfig from '../../config/apiConfig'
import { ASSET_CATALOG_FIELDS } from '../constants/metadata'
import { DEFAULT_ROWSPERPAGE } from '../constants/search'

const {
  search: {
    aiNaturalSearchTextUrl = '',
  } = {},
  betaSearch: {
    v3SearchUrl = '',
    searchCatalogUrl = '',
    restoreDeletedAssetsUrl = '',
    aiSearchUrl = '',
    savedSearchUrl = '',
  } = {},
  userDetails: { userPreferences = '' } = {},
  metadata: {
    assetMetadataUrl = '',
    drmUrl = '',
    assetDeleteUrl = '',
    catalogUrl = '',
    productUrl = '',
    updateAssetMetadataUrl = '',
  } = {},
} = apiConfig

export const SEARCH_METADATA_SESSION_STORAGE_KEY = 'assetLevelDetailsResponse'
export const SEARCH_METADATA_EXPIRATION_MS = 30 * 60 * 1000 // 30 minutes

export const getCachedSearchMetadata = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const cachedValue = window.sessionStorage.getItem(
    SEARCH_METADATA_SESSION_STORAGE_KEY,
  )

  if (!cachedValue) {
    return null
  }

  try {
    const parsed = JSON.parse(cachedValue)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    // Check expiration
    const { data, timestamp } = parsed
    if (!timestamp || Date.now() - timestamp > SEARCH_METADATA_EXPIRATION_MS) {
      window.sessionStorage.removeItem(SEARCH_METADATA_SESSION_STORAGE_KEY)
      return null
    }

    return data || null
  } catch {
    window.sessionStorage.removeItem(SEARCH_METADATA_SESSION_STORAGE_KEY)
    return null
  }
}

export const cacheSearchMetadata = (metadata = {}) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const cachePayload = {
      data: metadata || {},
      timestamp: Date.now(),
    }
    window.sessionStorage.setItem(
      SEARCH_METADATA_SESSION_STORAGE_KEY,
      JSON.stringify(cachePayload),
    )
  } catch {
    // Ignore quota/storage errors and continue with network-backed behavior.
  }
}

export const clearCachedSearchMetadata = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(SEARCH_METADATA_SESSION_STORAGE_KEY)
}

export async function searchAllAssets(payloadProps, signal) {
  const {
    payload = {},
    page = Number(0),
    rowsPerPage = DEFAULT_ROWSPERPAGE,
    savedSearchId = '',
  } = payloadProps

  const baseUrl = v3SearchUrl
  const apiKey = apiConfig.key

  const normalizedSavedSearchId =
    typeof savedSearchId === 'string' ? savedSearchId.trim() : ''

  const queryParams =
    `?page=${page}&per_page=${rowsPerPage}&key=${apiKey}` +
    (normalizedSavedSearchId
      ? `&saved_search_id=${encodeURIComponent(normalizedSavedSearchId)}`
      : '')

  const url = `${baseUrl}${queryParams}`

  return axios.post(url, payload, signal ? { signal } : undefined)
}

export async function aiSearchTextApi(payloadProps, signal) {
  return axios.post(
    aiSearchUrl + '?page=0&size=100',
    payloadProps,
    signal ? { signal } : undefined,
  )
}

export async function aiSearchNaturalTextApi(payloadProps) {
  const {
    payload = {},
    page = Number(0),
    rowsPerPage = DEFAULT_ROWSPERPAGE,
  } = payloadProps
  return axios.post(
    aiNaturalSearchTextUrl +
      '?key=' +
      apiConfig.key +
      '&page=' +
      page +
      '&size=' +
      rowsPerPage,
    payload,
  )
}

export async function fetchUserPreferencesApi() {
  return axios.get(userPreferences)
}

export async function callSavedSearchesApi(searchTerm = '') {
  const params = searchTerm
    ? queryString.stringify({ saved_search_name: searchTerm })
    : ''
  const query = params ? `?${params}&page=0&size=100` : '?page=0&size=100'
  return axios.get(`${savedSearchUrl}${query}`)
}

export async function createNewSavedSearchApi(payload = {}) {
  return axios.post(`${savedSearchUrl}`, payload)
}

export async function updateSavedSearchByIdApi(fullPayload = {}) {
  const { payload = {}, saved_search_id = '' } = fullPayload
  return axios.put(`${savedSearchUrl}/${saved_search_id}`, payload)
}

export async function fetchSavedSearchesById(savedSearchId = '') {
  return axios.get(`${savedSearchUrl}/${savedSearchId}`)
}

export async function deleteSavedSearchByIdApi(savedSearchId = '') {
  return axios.delete(`${savedSearchUrl}/${savedSearchId}`)
}

export async function updateUserPreferencesApi(payload = {}) {
  return axios.put(userPreferences, payload)
}

export async function fetchMetadataApi(assetId = '', scope = '', signal) {
  const requestConfig = signal ? { signal } : undefined
  if (scope) {
    return axios.get(
      `${assetMetadataUrl}${assetId}?scope=${scope}`,
      requestConfig,
    )
  }
  return axios.get(`${assetMetadataUrl}${assetId}`, requestConfig)
}

export async function updateMetadataApi(payload = {}) {
  return axios.put(`${updateAssetMetadataUrl}`, payload)
}

export async function fetchRevisionsApi(assetId = '', signal) {
  return axios.get(
    `${assetMetadataUrl}${assetId}/revisions`,
    signal ? { signal } : undefined,
  )
}

export async function fetchAssetCatalogApi() {
  return axios.get(
    `${catalogUrl}?catalogType=${ASSET_CATALOG_FIELDS.join(',')}`,
  )
}

export async function deleteRevisionsApi(payload = '') {
  return axios.delete(`${assetDeleteUrl}`, {
    data: payload,
  })
}

export async function fetchDrmDetailsApi(drmId = '') {
  return axios.get(`${drmUrl}/${drmId}`)
}

export async function fetchProductDetailsApi(fieldName = '', keyword = '') {
  return axios.get(`${productUrl}?${fieldName}=${keyword}`)
}

let _pendingFetchSearchMetadata = null

export async function fetchSearchMetadataApi(signal) {
  const cachedMetadata = getCachedSearchMetadata()

  if (cachedMetadata) {
    return { data: cachedMetadata }
  }

  if (_pendingFetchSearchMetadata) {
    return _pendingFetchSearchMetadata
  }

  _pendingFetchSearchMetadata = axios
    .get(`${searchCatalogUrl}`, signal ? { signal } : undefined)
    .then((response) => {
      const metadata = response?.data || {}
      cacheSearchMetadata(metadata)
      return response
    })
    .finally(() => {
      _pendingFetchSearchMetadata = null
    })

  return _pendingFetchSearchMetadata
}
export async function searchAllReversAssetsByImage(payloadProps, signal) {
  const {
    payload: { file, sort_fields = {} },
    reserveImageCount,
  } = payloadProps

  const apiKey = apiConfig.key
  const baseUrl = apiConfig.search.contentReverseImageSearchUrl

  const formData = new FormData()
  formData.append('file', file)

  const searchPayload = { sort_fields: { ...sort_fields } }
  formData.append(
    'assetSearchFiltersRequest',
    new Blob([JSON.stringify(searchPayload)], {
      type: 'application/json',
    }),
  )

  const query = `?key=${apiKey}&page=0&per_page=${reserveImageCount}`
  return axios.post(
    `${baseUrl}${query}`,
    formData,
    signal ? { signal } : undefined,
  )
}

export async function restoreDeletedAssetsApi(payload = {}) {
  return axios.post(`${restoreDeletedAssetsUrl}`, payload)
}
